// ── The standby calendar: month grid, drag strokes and the paint plan ────────
//
// Standby is planned months ahead, so the calendar shows a month at a time and
// an editor paints days with a person and a level. These are the pure parts:
// the grid a month occupies (whole weeks, Monday to Sunday), the dates a drag
// from one cell to another covers, and what a stroke would change against the
// cells already there. Shared with the tests, so it must stay free of React
// and the DOM.

const DAY_MS = 86400000;
const toISO = (d) => d.toISOString().slice(0, 10);
const parse = (iso) => new Date(`${iso}T00:00:00Z`);
const addDays = (iso, n) => toISO(new Date(parse(iso).getTime() + n * DAY_MS));

/** Monday of the week holding this date. */
function mondayOf(iso) {
  const d = parse(iso);
  const dow = (d.getUTCDay() + 6) % 7; // Monday = 0
  return addDays(iso, -dow);
}

/**
 * The grid for a month ('YYYY-MM'): rows of seven ISO dates, Monday first,
 * padded at both ends to whole weeks so a drag across the month edge behaves.
 */
export function monthGrid(ym) {
  const [y, m] = ym.split('-').map(Number);
  const first = `${ym}-01`;
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const last = `${ym}-${String(lastDay).padStart(2, '0')}`;
  const from = mondayOf(first);
  const to = addDays(mondayOf(last), 6);
  const weeks = [];
  for (let cursor = from; cursor <= to; cursor = addDays(cursor, 7)) {
    weeks.push(Array.from({ length: 7 }, (_, i) => addDays(cursor, i)));
  }
  return { ym, from, to, weeks, inMonth: (iso) => iso.slice(0, 7) === ym };
}

/** 'YYYY-MM' moved by n months, either way, across year ends. */
export function shiftMonth(ym, n) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function monthLabel(ym) {
  const [y, m] = ym.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export function monthOf(iso) {
  return iso.slice(0, 7);
}

/**
 * Every date from one end of a stroke to the other, inclusive, in order. A
 * drag that goes backwards, or across a row boundary, is the same stroke as
 * the tidy one: the calendar is a run of days, not a box.
 */
export function dragDates(a, b) {
  const [from, to] = a <= b ? [a, b] : [b, a];
  const out = [];
  for (let cursor = from; cursor <= to; cursor = addDays(cursor, 1)) out.push(cursor);
  return out;
}

const standbyIds = (sections) => new Set((sections || []).filter(s => s.kind === 'standby').map(s => s.id));

/**
 * What painting these dates with this person and level would do, against the
 * cells already there. Mirrors the server's rules so the dialog can say so
 * before the save: a day already at this level is left alone; a day at another
 * level in this row moves and its record goes back to pending; a day where the
 * person is on standby in another standby row is a clash the server refuses,
 * so it is left out of the stroke and named, rather than sinking the rest.
 */
export function paintPlan({ slots, sections, sectionId, personId, levelId, dates }) {
  const standby = standbyIds(sections);
  const mine = (slots || []).filter(s => s.person_id === personId && (s.level_id || s.standby_level_id));
  const unchanged = [], replaced = [], clashes = [], fresh = [];
  for (const d of dates) {
    const here = mine.find(s => s.slot_date === d && s.section_id === sectionId);
    const elsewhere = mine.some(s => s.slot_date === d && s.section_id !== sectionId && standby.has(s.section_id));
    if (elsewhere) clashes.push(d);
    else if (here && (here.level_id || here.standby_level_id) === levelId) unchanged.push(d);
    else if (here) replaced.push(d);
    else fresh.push(d);
  }
  const toSave = dates.filter(d => !clashes.includes(d));
  return { dates: toSave, unchanged, replaced, clashes, changes: fresh.length + replaced.length };
}

/**
 * Clearing is an eraser: whoever is on standby in this row on the dragged
 * days comes off, one save per person, so nobody has to pick a name first.
 * Other rows are untouched.
 */
export function clearPlan({ slots, sectionId, dates }) {
  const wanted = new Set(dates);
  const byPerson = new Map();
  for (const s of slots || []) {
    if (s.section_id !== sectionId || !(s.level_id || s.standby_level_id) || !wanted.has(s.slot_date)) continue;
    if (!byPerson.has(s.person_id)) byPerson.set(s.person_id, []);
    byPerson.get(s.person_id).push(s.slot_date);
  }
  const strokes = [...byPerson.entries()]
    .map(([personId, ds]) => ({ personId, dates: [...new Set(ds)].sort() }))
    .sort((a, b) => a.personId.localeCompare(b.personId));
  return { strokes, changes: strokes.reduce((n, st) => n + st.dates.length, 0) };
}
